/***
 *
 *   POPUP
 *   Overlay with a custom child component. Where MODAL is driven from
 *   ViewContext, this one is rendered inline by the view and controlled with
 *   a `show` prop.
 *
 *   Styling matches ui-reference/src/components/ui/dialog.tsx; focus trap,
 *   Escape and scroll lock come from useOverlay.
 *
 *   PROPS
 *   show: boolean
 *   title: (optional)
 *   text: message to the user (optional)
 *   onClose: called when the overlay is dismissed (optional)
 *
 **********/

import React, { useState, useEffect } from 'react';
import { Icon } from 'components/lib';
import { useOverlay } from 'components/hooks/useOverlay';
import Style from './popup.tailwind';

export const Popup = (props) => {
    const [show, setShow] = useState(props.show);

    useEffect(() => {
        setShow(props.show);
    }, [props.show]);

    if (!show) return null;

    return <PopupSurface {...props} onDismiss={() => setShow(false)} />;
};

/**
 * Split out so useOverlay's effects mount and unmount with the overlay itself
 * — hooks cannot sit behind the `if (!show)` return above.
 */
function PopupSurface(props) {
    const close = () => {
        props.onDismiss();
        props.onClose?.();
    };

    const ref = useOverlay({ onClose: close });

    return (
        <>
            <div className={Style.overlay} onClick={close} aria-hidden />

            <div className={Style.wrapper}>
                <div
                    ref={ref}
                    role="dialog"
                    aria-modal="true"
                    aria-label={props.title || 'Dialog'}
                    tabIndex={-1}
                    className={Style.content}>
                    {props.dismissible !== false && (
                        <button
                            type="button"
                            onClick={close}
                            aria-label="Close"
                            className="absolute right-4 top-4 rounded-sm text-text-secondary opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                            <Icon image="x" size={16} color="currentColor" />
                        </button>
                    )}

                    <div className={Style.top}>
                        {props.title && <h3 className={Style.title}>{props.title}</h3>}
                        {props.text && <hr className={Style.divider} />}
                        {props.text && <p className={Style.text}>{props.text}</p>}
                        {props.children}
                    </div>
                </div>
            </div>
        </>
    );
}
